import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_PLATFORM_SUPPORTED_LOCALES, GET_STORE_RECEIPT_SETTINGS } from "src/constants/queries";

export type PlatformLocale = {
  code: string;
  label: string;
  nativeLabel: string;
};

const DEFAULT_LOCALE = "en-US";

export const useStorePosLocales = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const [platformLocales, setPlatformLocales] = useState<PlatformLocale[]>([]);
  const [enabledLocales, setEnabledLocales] = useState<string[]>([DEFAULT_LOCALE]);
  const [defaultLocale, setDefaultLocale] = useState<string>(DEFAULT_LOCALE);
  const [loading, setLoading] = useState(false);

  const fetchLocales = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: platformData }, storeData] = await Promise.all([
        axBe.post(GRAPHQL_ENDPOINT, { query: GET_PLATFORM_SUPPORTED_LOCALES }),
        hasRestaurant?.correlationId
          ? axBe.post(GRAPHQL_ENDPOINT, {
              query: GET_STORE_RECEIPT_SETTINGS,
              variables: {
                retrieveStoresInput: { storeCorrelationIds: [hasRestaurant.correlationId] },
              },
            })
          : Promise.resolve(null),
      ]);

      setPlatformLocales(platformData?.data?.platformSupportedLocales || []);

      const store = storeData?.data?.data?.stores?.[0];
      const storeEnabled =
        Array.isArray(store?.enabledLocales) && store.enabledLocales.length > 0
          ? store.enabledLocales
          : [DEFAULT_LOCALE];
      setEnabledLocales(storeEnabled);
      setDefaultLocale(store?.defaultLocale || storeEnabled[0] || DEFAULT_LOCALE);
    } catch (error) {
      console.error("Error fetching store POS locales:", error);
    } finally {
      setLoading(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    fetchLocales();
  }, [fetchLocales]);

  const enabledLocaleOptions = useMemo(
    () =>
      platformLocales.filter((locale) => enabledLocales.includes(locale.code)),
    [platformLocales, enabledLocales]
  );

  return {
    platformLocales,
    enabledLocales,
    defaultLocale,
    enabledLocaleOptions,
    loading,
    refresh: fetchLocales,
  };
};
